import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Stack,
  Title,
  Paper,
  Group,
  Button,
  Text,
  Alert,
  LoadingOverlay,
  Card,
  Grid,
  ActionIcon,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconClipboardList,
  IconAlertCircle,
  IconPencil,
} from '@tabler/icons-react';
import { useMedplum, useResource } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { Specimen, Patient, ServiceRequest } from '@medplum/fhirtypes';
import { formatDateTime, resolveId } from '@medplum/core';
import { StatusBadge } from '@/components/medplum-react/StatusBadge/StatusBadge';
import { OrderUtils } from '../utils/order-table.utils';
import { SpecimenDisplayCard } from './SpecimenDisplayCard';
import { SpecimenCreateEditDrawer } from './SpecimenCreateEditDrawer';

export function SpecimenDetailsPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { t } = useTranslation('lims');
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [linkedOrders, setLinkedOrders] = useState<ServiceRequest[]>([]);

  // Load the specimen
  const specimen = useResource<Specimen>({ reference: `Specimen/${id}` });

  // Load the patient
  const patient = useResource<Patient>(
    specimen?.subject?.reference
      ? { reference: specimen.subject.reference }
      : undefined
  );

  // Load linked orders
  useEffect(() => {
    if (!specimen?.request?.length) {
      return;
    }

    const loadLinkedOrders = async () => {
      setLoading(true);
      try {
        const orders: ServiceRequest[] = [];
        for (const requestRef of specimen.request || []) {
          const orderId = resolveId(requestRef);
          if (!orderId) {
            continue;
          }
          try {
            const order = await medplum.readResource('ServiceRequest', orderId);
            orders.push(order);
          } catch (error) {
            console.error(`Failed to load order ${orderId}:`, error);
          }
        }
        setLinkedOrders(orders);
      } catch (error) {
        console.error('Error loading linked orders:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLinkedOrders();
  }, [specimen?.request, medplum]);

  const handleSpecimenUpdated = () => {
    setEditModalOpen(false);
    // Refresh the specimen data by re-fetching
    if (specimen?.id) {
      medplum.invalidateSearches('Specimen');
    }
  };

  // ---------------------------------------------------------------------------
  if (!specimen) {
    return (
      <div style={{ position: 'relative', height: '400px' }}>
        <LoadingOverlay visible />
      </div>
    );
  }

  return (
    <Stack gap="md" className="p-6">
      <Group justify="space-between">
        <Group>
          <ActionIcon variant="subtle" onClick={() => navigate(-1)}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={1}>{t('specimenDetails.title')}</Title>
        </Group>
        <Button
          variant="light"
          leftSection={<IconPencil size={16} />}
          onClick={() => setEditModalOpen(true)}
        >
          {t('specimenDetails.editSpecimen')}
        </Button>
      </Group>

      <Grid>
        <Grid.Col span={8}>
          {/* Main Specimen Information */}
          <SpecimenDisplayCard specimen={specimen} patient={patient} />
        </Grid.Col>

        <Grid.Col span={4}>
          {/* Linked Orders */}
          <Paper shadow="sm" p="md" withBorder>
            <Stack gap="md">
              <Group>
                <IconClipboardList size={20} color="green" />
                <Text fw={600}>{t('specimenDetails.linkedOrders')}</Text>
              </Group>

              {loading ? (
                <div style={{ position: 'relative', height: '100px' }}>
                  <LoadingOverlay visible />
                </div>
              ) : linkedOrders.length > 0 ? (
                <Stack gap="sm">
                  {linkedOrders.map((order) => (
                    <Card key={order.id} padding="sm" withBorder>
                      <Stack gap="xs">
                        <Group justify="space-between">
                          <Text fw={500} size="sm">
                            {order.identifier?.[0]?.value || order.id}
                          </Text>
                          <StatusBadge
                            status={order.status || 'unknown'}
                            label={t(OrderUtils.getStatusName(order.status))}
                          />
                        </Group>
                        <Text size="xs" c="dimmed">
                          {order.code?.coding?.[0]?.display ||
                            t('specimenDetails.unknownTest')}
                        </Text>{' '}
                        {order.authoredOn && (
                          <Text size="xs" c="dimmed">
                            {t('specimenDetails.ordered')}:{' '}
                            {formatDateTime(order.authoredOn)}
                          </Text>
                        )}
                        <Button
                          variant="subtle"
                          size="xs"
                          onClick={() => navigate(`/lims/order/${order.id}`)}
                        >
                          {t('specimenDetails.viewOrder')}
                        </Button>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Alert icon={<IconAlertCircle size={16} />} color="gray">
                  <Text size="sm">{t('specimenDetails.noOrdersLinked')}</Text>
                </Alert>
              )}
            </Stack>
          </Paper>

          {/* System Information */}
          <Paper shadow="sm" p="md" withBorder mt="md">
            <Stack gap="md">
              <Text fw={600}>{t('specimenDetails.systemInformation')}</Text>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    {t('specimenDetails.resourceId')}
                  </Text>
                  <Text size="sm" ff="monospace">
                    {specimen.id}
                  </Text>
                </Group>
                {specimen.meta?.lastUpdated && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      {t('specimenDetails.lastUpdated')}
                    </Text>{' '}
                    <Text size="sm">
                      {formatDateTime(specimen.meta.lastUpdated)}
                    </Text>
                  </Group>
                )}
                {specimen.meta?.versionId && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      {t('specimenDetails.version')}
                    </Text>
                    <Text size="sm" ff="monospace">
                      {specimen.meta.versionId}
                    </Text>
                  </Group>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Edit Modal */}
      <SpecimenCreateEditDrawer
        opened={editModalOpen}
        onClose={handleSpecimenUpdated}
        specimen={specimen}
        patient={patient}
      />
    </Stack>
  );
}
